<svelte:options runes={false} />
<script>
  import { orders, formatCurrency, formatCrypto } from '../../lib/store.js';
  import { selectedOrgId } from '../../lib/orgContext.js';
  import { getCryptoIconOrLetters, getCryptoGradient } from '../../lib/cryptoIcons.js';
  import { groupChildren, withUniqueKeys, statusTone, isWorking, STATUS_LABELS, fmtQty } from '../../lib/twapReport.js';
  import api from '../../lib/api.js';
  import SlideInPanel from '../AutoTrading/SlideInPanel.svelte';
  import TwapReport from '../shared/twap/TwapReport.svelte';
  import { createTwapParentsLoader } from './ordersTwapParents.js';

  // Map order status enum values to readable strings
  const OrderStatus = {
    0: 'Placing',
    1: 'Accepted',
    2: 'Filled',
    3: 'Partial Filled',
    4: 'Done',
    5: 'Rejected',
    6: 'Canceled',
    7: 'Stopped',
    8: 'Not Found',
    9: 'In-Queue',
    10: 'Waiting'
  };

  // Map action enum to readable strings
  const ActionType = {
    1: 'Buy',
    2: 'Sell'
  };

  // Map order type enum to readable strings
  const OrderType = {
    1: 'Market',
    2: 'Limit',
    3: 'Stop',
    4: 'Stop Limit',
    5: 'Stop Loss',
    6: 'Take Profit',
    7: 'Limit Maker',
    8: 'TWAP'
  };

  // Status colors
  function getStatusColor(status) {
    if ([2, 4].includes(status)) return 'success'; // Filled, Done
    if ([5, 6, 7, 8].includes(status)) return 'danger'; // Rejected, Canceled, Stopped, Not Found
    if ([0, 1, 3, 9, 10].includes(status)) return 'warning'; // In progress
    return '';
  }

  // Format datetime from UTC to local time
  function formatTime(utcDateTime) {
    if (!utcDateTime) return '-';

    try {
      const date = new Date(utcDateTime);
      // Check if date is valid
      if (isNaN(date.getTime())) return '-';

      // Get today's date
      const today = new Date();
      const isToday = date.toDateString() === today.toDateString();

      // Format time
      if (isToday) {
        // Show only time for today's orders
        return date.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        });
      } else {
        // Show date and time for older orders
        return date.toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });
      }
    } catch (error) {
      console.error('Error formatting time:', error);
      return '-';
    }
  }

  // Format symbol from BTC-USD to BTC/USD
  function formatSymbol(symbol) {
    if (!symbol) return '-';
    // Replace dash with forward slash
    return symbol.replace('-', '/');
  }

  // Get icon info for crypto symbol
  function getIconInfo(symbol) {
    if (!symbol) return { type: 'letters', value: 'CR' };
    // Extract base asset from symbol (e.g., BTC from BTC-USD or BTC/USD)
    const baseAsset = symbol.split(/[-\/]/)[0];
    return getCryptoIconOrLetters(baseAsset, baseAsset);
  }

  // Get gradient for crypto symbol
  function getGradient(symbol) {
    if (!symbol) return getCryptoGradient('DEFAULT');
    const baseAsset = symbol.split(/[-\/]/)[0];
    return getCryptoGradient(baseAsset);
  }

  // This component RENDERS the orders store; it does not load it. It used to define its own
  // unscoped loadOrders() that wrote the same store on mount and on Refresh, overwriting the
  // correctly org-scoped data from App.svelte with the caller's own org's — the reported bug.
  // Refresh now asks the owner to reload, so there is exactly one writer and one org scope.
  export let onRefresh = null;

  function refresh() {
    onRefresh?.();
    loadParents();
  }

  // ----- TWAP parents (TASK-20260823-twap-strategies-tca AC 25) ----------------------------------
  // The venue's order list carries the children (each stamped with its ParentOrderId); the parent's
  // own progress/status is the durable TwapParentOrders row read through /api/twap/parents. This
  // component owns that read (it is the only consumer) and guards it the same way the App-level
  // loaders do: the org the request was issued for must still be the selected org when it lands.
  let parents = [];
  let parentsError = null;

  // The ONE tenant-scoped-load predicate (TASK-20260818 F1/F8), via the extracted+tested factory:
  // it skips entirely while the org is unresolved — a scope-less read is a guaranteed 400 (ADR-0023)
  // and the startup window, when $orders lands before $selectedOrgId resolves, is normal — and it
  // drops any response (success OR failure) that arrives after the org moved on.
  const loadParents = createTwapParentsLoader({
    getOrgId: () => $selectedOrgId,
    api,
    apply: (items) => { parents = items; parentsError = null; },
    onError: (e) => { parents = []; parentsError = e?.message ?? 'TWAP parents unavailable'; },
  });

  // Read the parents on init and whenever the orders list is re-fetched (the App refetches it on every SSE
  // `order` push, so a working parent's progress advances with its children) or the org changes.
  // Re-read on a new orders payload AND on an org change: keying off $orders alone would miss a
  // tenant switch that leaves the orders reference stable (AC 5).
  let lastOrdersRef = null;
  let lastParentsOrgId = null;
  $: if ($orders !== lastOrdersRef || $selectedOrgId !== lastParentsOrgId) {
    lastOrdersRef = $orders;
    lastParentsOrgId = $selectedOrgId;
    loadParents();
  }

  $: groups = groupChildren($orders, parents);
  // Keyed rows: a venue payload with a missing or repeated orderId must still render (Svelte 5 throws on
  // duplicate/undefined keys), so every key is made unique — see withUniqueKeys.
  $: keyedGroups = withUniqueKeys(groups, (g) => (g.kind === 'parent' ? `p:${g.id}` : `o:${g.order?.orderId ?? ''}`));
  $: hasRows = groups.length > 0;

  // Collapsed-by-default children per parent; toggled per row.
  let expanded = {};
  function toggle(id) { expanded = { ...expanded, [id]: !expanded[id] }; }

  // Report panel (the existing SlideInPanel).
  let reportFor = null;   // { id, summary }
  function openReport(g) { reportFor = { id: g.id, summary: g.summary }; }
  function closeReport() { reportFor = null; }

  // Cancel with an explicit confirm (design-system: financial actions never silently fire).
  let confirmCancelId = null;
  let cancelBusy = null;
  let cancelNote = {};
  async function confirmCancel(g) {
    if (cancelBusy) return;
    cancelBusy = g.id;
    try {
      const r = await api.cancelTwapParent(g.id, $selectedOrgId);
      cancelNote = { ...cancelNote, [g.id]: r?.accepted
        ? (r.pending ? 'Cancel requested — the owning host honours it on its next cycle.' : `Cancelled (${r.status ?? 'done'}).`)
        : `Cancel not accepted (${r?.status ?? 'unknown'}).` };
      confirmCancelId = null;
      await loadParents();
      onRefresh?.();
    } catch (e) {
      cancelNote = { ...cancelNote, [g.id]: e?.message ?? 'Cancel failed' };
    } finally {
      cancelBusy = null;
    }
  }

  const parentStatusLabel = (g) => (g.summary ? (STATUS_LABELS[g.summary.status] ?? g.summary.status) : (g.order ? (OrderStatus[g.order.status] ?? '—') : '—'));
  const canCancel = (g) => g.summary ? isWorking(g.summary.status) && !g.summary.cancelRequestedAt : (g.order ? [0, 1, 3, 9, 10].includes(g.order.status) : false);
</script>

<div class="orders">
  <div class="orders-header">
    <h2>Orders</h2>
    <button class="refresh-btn" on:click={refresh} aria-label="Refresh orders">
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path d="M12 2v4h-4M2 12v-4h4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        <path d="M2.51 5a5 5 0 018.85-1.36L12 4M2 10l.64.36A5 5 0 0011.49 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
      </svg>
    </button>
  </div>

  <div class="orders-container">
    {#if hasRows}
      <div class="orders-table">
        <div class="orders-table-header">
          <span></span>
          <span>Symbol</span>
          <span>Side</span>
          <span>Type</span>
          <span>Quantity</span>
          <span>Price</span>
          <span>Total</span>
          <span>Status</span>
          <span>Time</span>
        </div>

        <div class="orders-table-body">
          {#each keyedGroups as { key, item: g } (key)}
            {#if g.kind === 'single'}
              {@const order = g.order}
              <div class="orders-row">
                <div class="order-icon">
                  {#if getIconInfo(order.symbol).type === 'icon'}
                    <div class="crypto-icon icon-symbol" style="background: {getGradient(order.symbol)}">
                      {getIconInfo(order.symbol).value}
                    </div>
                  {:else}
                    <div class="crypto-icon" style="background: {getGradient(order.symbol)}">
                      {getIconInfo(order.symbol).value}
                    </div>
                  {/if}
                </div>

                <div class="order-symbol">{formatSymbol(order.symbol)}</div>

                <div class="order-side {ActionType[order.action]?.toLowerCase()}">
                  {ActionType[order.action] || '-'}
                </div>

                <div class="order-type">
                  {OrderType[order.tradeOrderType] || '-'}
                </div>

                <div class="order-quantity">
                  {order.qty ? formatCrypto(order.qty, 8) : '-'}
                </div>

                <div class="order-price">
                  {order.price ? formatCurrency(order.price) : 'Market'}
                </div>

                <div class="order-amount">
                  {order.amount ? formatCurrency(order.amount) : formatCurrency((order.qty || 0) * (order.price || 0))}
                </div>

                <div class="order-status {getStatusColor(order.status)}">
                  <span class="status-badge">
                    {OrderStatus[order.status] || `Unknown (${order.status})`}
                  </span>
                </div>

                <div class="order-time">
                  {formatTime(order.filledOrCancelledTime || order.createdAt)}
                </div>
              </div>
            {:else}
              {@const s = g.summary}
              {@const working = s ? isWorking(s.status) : false}
              <div class="orders-row twap-parent" class:twap-working={working}>
                <div class="order-icon">
                  <button type="button" class="twap-expand" aria-expanded={!!expanded[g.id]} aria-controls={`twap-children-${g.id}`}
                    aria-label={`${expanded[g.id] ? 'Collapse' : 'Expand'} ${g.children.length} slices`} on:click={() => toggle(g.id)}>
                    {expanded[g.id] ? '▾' : '▸'}
                  </button>
                </div>
                <div class="order-symbol">{formatSymbol(g.symbol)} <span class="twap-tag">TWAP</span></div>
                <div class="order-side {String(g.side ?? '').toLowerCase()}">{g.side ?? '-'}</div>
                <div class="order-type">{s ? `${s.source}` : 'parent'}</div>
                <div class="order-quantity twap-progress-cell">
                  <div class="twap-progress" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow={g.progress.pct ?? 0}
                    aria-label={`Filled ${fmtQty(g.progress.filledQty)} of ${fmtQty(g.progress.targetQty)}`}>
                    <span class="twap-progress-fill" style={`width: ${g.progress.pct ?? 0}%`}></span>
                  </div>
                  <span class="twap-progress-text">
                    {g.progress.filledQty !== null ? `${fmtQty(g.progress.filledQty)} / ${fmtQty(g.progress.targetQty ?? s?.targetAmount)}` : `${g.children.length} slices`}
                    {#if g.progress.cyclesCompleted !== null} · {g.progress.cyclesCompleted}{g.progress.cyclesPlanned ? `/${g.progress.cyclesPlanned}` : ''} cyc{/if}
                  </span>
                </div>
                <div class="order-price">{s?.avgFillPrice ? formatCurrency(s.avgFillPrice) : '—'}</div>
                <div class="order-amount">{s?.filledAmount ? formatCurrency(s.filledAmount) : '—'}</div>
                <div class="order-status">
                  <span class={`status-badge twap-status twap-status-${s ? statusTone(s.status) : 'neutral'}`}>{parentStatusLabel(g)}</span>
                  {#if s?.cancelRequestedAt}<span class="twap-note">cancel requested</span>{/if}
                </div>
                <div class="order-time twap-actions">
                  <button type="button" class="btn btn-ghost btn-sm" on:click={() => openReport(g)}>Report</button>
                  {#if canCancel(g)}
                    {#if confirmCancelId === g.id}
                      <span class="twap-confirm" role="group" aria-label="Confirm cancel">
                        <button type="button" class="btn btn-danger btn-sm" disabled={cancelBusy === g.id} on:click={() => confirmCancel(g)}>{cancelBusy === g.id ? 'Cancelling…' : 'Confirm'}</button>
                        <button type="button" class="btn btn-ghost btn-sm" on:click={() => (confirmCancelId = null)}>Keep</button>
                      </span>
                    {:else}
                      <button type="button" class="btn btn-ghost btn-sm twap-cancel" on:click={() => (confirmCancelId = g.id)}>Cancel</button>
                    {/if}
                  {/if}
                </div>
              </div>
              {#if cancelNote[g.id]}
                <div class="twap-note-row" role="status">{cancelNote[g.id]}</div>
              {/if}
              {#if expanded[g.id]}
                <div id={`twap-children-${g.id}`} class="twap-children">
                  {#each withUniqueKeys(g.children, (c) => c.orderId) as { key: childKey, item: c } (childKey)}
                    <div class="orders-row twap-child">
                      <div class="order-icon"><span class="twap-child-mark" aria-hidden="true">└</span></div>
                      <div class="order-symbol twap-child-id" title={c.orderId}>{formatSymbol(c.symbol)}</div>
                      <div class="order-side {ActionType[c.action]?.toLowerCase()}">{ActionType[c.action] || '-'}</div>
                      <div class="order-type">{OrderType[c.tradeOrderType] || '-'}</div>
                      <div class="order-quantity">{c.qty ? formatCrypto(c.qty, 8) : '-'}</div>
                      <div class="order-price">{c.price ? formatCurrency(c.price) : 'Market'}</div>
                      <div class="order-amount">{c.amount ? formatCurrency(c.amount) : formatCurrency((c.qty || 0) * (c.price || 0))}</div>
                      <div class="order-status {getStatusColor(c.status)}"><span class="status-badge">{OrderStatus[c.status] || `Unknown (${c.status})`}</span></div>
                      <div class="order-time">{formatTime(c.filledOrCancelledTime || c.createdAt)}</div>
                    </div>
                  {:else}
                    <div class="twap-note-row">No slices at the venue yet.</div>
                  {/each}
                </div>
              {/if}
            {/if}
          {/each}
        </div>
      </div>
    {:else}
      <div class="orders-empty">
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
          <rect x="8" y="12" width="32" height="24" rx="2" stroke="currentColor" stroke-width="2" opacity="0.3"/>
          <path d="M16 20h16M16 24h16M16 28h8" stroke="currentColor" stroke-width="2" stroke-linecap="round" opacity="0.3"/>
        </svg>
        <p class="orders-empty-title">No orders yet</p>
        <span class="orders-empty-description">Your orders will appear here</span>
      </div>
    {/if}
    {#if parentsError}
      <div class="twap-note-row twap-error" role="status">TWAP parents unavailable: {parentsError}</div>
    {/if}
  </div>
</div>

<SlideInPanel open={reportFor !== null} title={reportFor ? `TWAP report · ${formatSymbol(reportFor.summary?.symbol ?? '')}` : 'TWAP report'} on:close={closeReport}>
  {#if reportFor}
    <TwapReport parentId={reportFor.id} orgId={$selectedOrgId} initial={reportFor.summary} />
  {/if}
</SlideInPanel>

<style>
  @import './Orders.css';

  /* ---- TWAP parent grouping (TASK-20260823-twap-strategies-tca) ---- */
  .twap-parent { background: var(--bg-secondary); }
  .twap-working { border-left: 3px solid var(--color-warning); }
  .twap-expand {
    all: unset; cursor: pointer; width: 24px; height: 24px; display: inline-flex; align-items: center; justify-content: center;
    border-radius: var(--radius-sm); color: var(--text-secondary); border: 1px solid var(--border-secondary);
  }
  .twap-expand:focus-visible { outline: 2px solid var(--color-primary); outline-offset: 1px; }
  .twap-tag { font-size: var(--font-size-xs); letter-spacing: 0.06em; color: var(--color-primary); border: 1px solid var(--color-primary); border-radius: 999px; padding: 0 0.4rem; margin-left: 0.3rem; }
  .twap-progress-cell { display: flex; flex-direction: column; gap: 0.2rem; min-width: 0; }
  .twap-progress { height: 5px; border-radius: 999px; background: var(--bg-quaternary); overflow: hidden; }
  .twap-progress-fill { display: block; height: 100%; background: var(--color-primary); transition: width var(--transition-base); }
  @media (prefers-reduced-motion: reduce) { .twap-progress-fill { transition: none; } }
  .twap-progress-text { font-size: var(--font-size-xs); color: var(--text-secondary); font-variant-numeric: tabular-nums; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .twap-status { text-transform: uppercase; letter-spacing: 0.04em; }
  .twap-status-working { color: var(--color-warning); border: 1px solid var(--color-warning); }
  .twap-status-done { color: var(--color-success); border: 1px solid var(--color-success); }
  .twap-status-partial { color: var(--color-primary); border: 1px solid var(--color-primary); }
  .twap-status-cancelled { color: var(--text-secondary); border: 1px solid var(--border-secondary); }
  .twap-status-failed { color: var(--color-danger); border: 1px solid var(--color-danger); }
  .twap-status-neutral { color: var(--text-secondary); border: 1px solid var(--border-secondary); }
  .twap-note { display: block; font-size: var(--font-size-xs); color: var(--text-muted); }
  .twap-actions { display: flex; gap: 0.3rem; flex-wrap: wrap; align-items: center; }
  .twap-confirm { display: inline-flex; gap: 0.3rem; }
  .twap-cancel { color: var(--color-danger); }
  .twap-note-row { padding: 0.3rem var(--spacing-base) 0.3rem 3.25rem; font-size: var(--font-size-xs); color: var(--text-secondary); border-bottom: 1px solid var(--border-secondary); }
  .twap-error { color: var(--color-warning); }
  .twap-children { border-left: 3px solid var(--border-tertiary); }
  .twap-child { background: var(--bg-primary); font-size: var(--font-size-xs); }
  .twap-child-mark { color: var(--text-muted); padding-left: 0.5rem; }
  .twap-child-id { color: var(--text-secondary); }
</style>
